/*-----------------------------------------------------------------
- Cart Mixin
-----------------------------------------------------------------*/

export default {
    name: 'CartMixin',

    /**
     * @inheritDoc
     */
    data() {
        return {
            model       : {},
            invoiceModel: {
                wares_count  : this.$store.getters['cart/myCount'],
                wares_price  : 0,
                discount     : 0,
                cost_shipping: undefined,
                payable_price: 0,
            },

            myCart            : [],
            coupons           : null,
            shippingMethods   : null,
            paymentPathOnline : null,
            paymentPathOffline: null,
            productsType      : 'products',

            pageTitle: this.$t('cart.title'),
        };
    },

    watch: {
        myCart: {
            /**
             * Add order list is to model
             *
             * @param {Array} newValue
             * @param {Array} oldValue
             */
            handler(newValue, oldValue) {
                if (!this.isEqual(oldValue, newValue)) {
                    this.model.id = this.get(newValue, '0.id');
                }
            },
            deep: true,
        },

        invoiceModel: {
            /**
             * Calculate payable price
             */
            handler() {
                if (this.invoiceModel.cost_shipping !== undefined) {
                    this.invoiceModel.payable_price = this.invoiceModel.wares_price - this.invoiceModel.discount + this.invoiceModel.cost_shipping;
                } else {
                    this.invoiceModel.payable_price = this.invoiceModel.wares_price - this.invoiceModel.discount;
                }
                this.invoiceModel.wares_count = this.$store.getters['cart/wareCount'];
                this.model.payable_amount = this.invoiceModel.payable_price;
            },
            deep     : true,
            immediate: true,
        },
    },

    /**
     * @inheritDoc
     */
    created() {
        if (this.$route.name === 'cart') {
            if (this.$store.getters['cart/myCart'].length) {
                if (this.$store.getters['auth/isLoggedIn']) {
                    this.addItems(this.productsType, this.$store.getters['cart/myCart']);
                } else {
                    this.myCart = [{
                        id   : '',
                        items: this.$store.getters['cart/myCart'],
                    }];
                    this.updateWaresPrice();
                }
            } else if (this.$store.getters['auth/isLoggedIn']) {
                this.refreshList(this.productsType);
            }
        }
    },

    methods: {
        /**
         * Add ware item to cart ( in Store )
         *
         * @param {object} ware - ware item
         */
        addToCart(ware) {
            const isLoggedIn = this.$store.getters['auth/isLoggedIn'];
            this.$wait.start('loading-button');

            if (isLoggedIn) {
                this.addItem(ware);
            } else {
                this.$store.dispatch('cart/updateMyCart', [ware]);
                if (ware.count) {
                    this.$toast.success(this.$t('cart.add_to_cart'));
                } else {
                    this.$toast.info(this.$t('cart.delete.success'));
                }
                this.$wait.end('loading-button');
            }
        },

        /**
         * Post ware item to cart ( in Database )
         *
         * @param {object} item - ware item
         * @returns {Promise}
         */
        addItem(item) {
            const { token } = this.$store.getters['auth/state'];
            this.$http.defaults.headers.common.Authorization = `Bearer ${token}`;

            return this.$services.Cart.myOrderAddItem(item).then((response) => {
                this.$toast.success(this.$t('cart.add_to_cart'));
                this.$store.dispatch('cart/emptyCart');
                this.$store.dispatch('cart/updateMyCart', this.get(response.data.results, 'items', []));
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('loading-button');
            });
        },
            
        /**
         * Fetch payment path list
         * 
         * @param {number | string} id - cart`s id
         * @param {string} type - type of payment path | Accepted Values: _online, offline_
         * @returns {Promise}
         */
        fetchPaymentPath(id, type = 'online') {
            this.paymentPathOnline = null;
            this.paymentPathOffline = null;
            this.$wait.start('loading-payment-path');
            return this.$services.Cart.paymentPathList({ id, type }).then((responses) => {
                if (type === 'online') {
                    this.paymentPathOnline = responses.data.results;
                } else if (type === 'offline') {
                    this.paymentPathOffline = responses.data.results;
                }

                this.afterSuccessFetchPaymentPath(responses, type);
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('loading-payment-path');
            });
        },

        /**
         * Custom Fetch Payment Path
         * Should be overwritten in cart page
         * 
         * @param {object} response
         * @param {string} type - type of payment path
         */
        afterSuccessFetchPaymentPath(response, type) { // eslint-disable-line no-unused-vars
        
        },

        /**
         * Fetch Shipping Methods
         *
         * @param {object} selectedAddress - Address that select by customer for shipping
         * @param {number | string} id - cart`s id
         * @returns {Promise}
         */
        fetchShippingMethods(selectedAddress, id) {
            this.model.address = selectedAddress.id;
            this.shippingMethods = null;

            this.$wait.start('loading-shipping-methods');
            return this.$services.Cart.shippingSuitableMethods({
                id,
                address: selectedAddress.id,
            }).then((response) => {
                this.shippingMethods = response.data.results;

                this.afterSuccessFetchShippingMethods();
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('loading-shipping-methods');
            });
        },

        /**
         * Custom Fetch Shipping Methods
         * Should be overwritten in cart page
         */
        afterSuccessFetchShippingMethods() {
        
        },

        /**
         * Add ware items to cart
         *
         * @param {string} type - products type
         * @param {Array} items - cart items
         * @param {boolean} hasLoading - Start loading
         * @param {string} loadingSlug
         * @returns {Promise}
         */
        addItems(type, items, hasLoading = true, loadingSlug) {
            if (!this.$store.getters['auth/isLoggedIn']) {
                this.$store.dispatch('cart/updateMyCart', items);
                return this.updateWaresPrice();
            }

            if (hasLoading) {
                this.$wait.start('loading-cart-page');
            }
            if (loadingSlug) {
                this.$wait.start(loadingSlug);
            }
            return this.$services.Cart.myOrderAddItems({
                type,
                items,
            }).then((response) => {
                if (this.get(response.data.results, 'items', []).length) {
                    this.myCart = [{
                        id   : this.get(response.data.results, 'id', ''),
                        items: this.get(response.data.results, 'items', []),
                    }];
                } else {
                    this.myCart = [];
                }

                this.$store.dispatch('cart/emptyCart');
                this.$store.dispatch('cart/updateMyCart', this.get(response.data.results, 'items', []));
                this.updateWaresPrice();

                this.afterSuccessAddItems(response);
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                if (hasLoading) {
                    this.$wait.end('loading-cart-page');
                }
                if (loadingSlug) {
                    this.$wait.end(loadingSlug);
                }
            });
        },

        /**
         * Custom Add Items
         * Should be overwritten in cart page
         * 
         * @param {object} response
         */
        afterSuccessAddItems(response) { // eslint-disable-line no-unused-vars
        
        },

        /**
         * Refresh order list
         * 
         * @param {string} type - products type
         * @param {Array} including
         * @returns {Promise}
         */
        refreshList(type, including = ['items']) {
            this.$wait.start('loading-cart-page');
            return this.$services.Cart.myOrderRefresh({ type, including }).then((response) => {
                if (this.get(response.data.results, 'items', []).length) {
                    this.myCart = [{
                        id   : this.get(response.data.results, 'id', ''),
                        items: this.get(response.data.results, 'items', []),
                    }];
                } else {
                    this.myCart = [];
                }

                this.$store.dispatch('cart/emptyCart');
                this.$store.dispatch('cart/updateMyCart', this.get(response.data.results, 'items', []));
                this.updateWaresPrice();

                this.afterSuccessRefreshList(response);
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('loading-cart-page');
            });
        },

        /**
         * Custom Refresh List
         * Should be overwritten in cart page
         * 
         * @param {object} response
         */
        afterSuccessRefreshList(response) { // eslint-disable-line no-unused-vars
        
        },

        /**
         * Remove ware item
         *
         * @param {object} ware - ware for remove
         * @returns {Promise}
         */
        removeItem(ware) {
            if (!this.$store.getters['auth/isLoggedIn']) {
                this.$store.dispatch('cart/removeWareItem', ware.ware_id);
                this.closeModal();
                return this.$toast.success(this.$t('cart.delete.success'));
            }
            this.$wait.start('loading-remove-item');
            return this.$services.Cart.myOrderRemoveItem({
                id: ware.id,
            }).then((response) => {
                if (this.get(response.data.results, 'items', []).length > 0) {
                    this.myCart = [{
                        id   : this.get(response.data.results, 'id', ''),
                        items: this.get(response.data.results, 'items', []),
                    }];
                } else {
                    this.myCart = [];
                }
                this.$store.dispatch('cart/emptyCart');
                this.$store.dispatch('cart/updateMyCart', this.get(response.data.results, 'items', []));
                this.updateWaresPrice();
                this.$toast.success(this.$t('cart.delete.success'));

                this.afterSuccessRemoveItem(response);
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('loading-remove-item');
            });
        },

        /**
         * Custom Remove Item
         * Should be overwritten in cart page
         * 
         * @param {object} response
         */
        afterSuccessRemoveItem(response) { // eslint-disable-line no-unused-vars
        
        },

        /**
         * Clear cart
         * 
         * @param {string} type - products type
         * @returns {Promise}
         */
        clearCart(type) {
            if (!this.$store.getters['auth/isLoggedIn']) {
                this.$store.dispatch('cart/emptyCart');
                this.myCart = [];
                this.invoiceModel.wares_price = 0;
                return this.$toast.success(this.$t('cart.delete.all_success'));
            }
            this.$wait.start('loading-button');
            return this.$services.Cart.myOrderEvacuate({ type }).then(() => {
                this.myCart = [];
                this.$store.dispatch('cart/emptyCart');
                this.updateWaresPrice();
                this.invoiceModel.wares_price = 0;
                this.$toast.success(this.$t('cart.delete.all_success'));

                this.afterSuccessClearCart();
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('loading-button');
            });
        },

        /**
         * Custom Clear Cart
         * Should be overwritten in cart page
         */
        afterSuccessClearCart() {
        
        },

        /**
         * Finalize Order
         * 
         * @returns {Promise}
         */
        finalizeOrder() {
            this.$wait.start('loading-payment-button');
            const params = {
                id             : this.model.id,
                shipping_method: this.model.shipping_method,
                shipping_slot  : this.model.shipping_slot,
                address        : this.model.address,
                payable_amount : this.model.payable_amount,
            };

            if (this.coupons) {
                params.coupons = this.coupons;
            }
            return this.$services.Cart.orderFinalize(params).then(() => {
                this.afterSuccessFinalizeOrder();
            }).catch((error) => {
                this.$wait.end('loading-payment-button');
                this.handleError(error);
                // Reload cart page, because one of the items in the cart has been updated.
                if (error.response.data.errorCode === 'cart-1008') {
                    window.location.reload();
                }
            });
        },

        /**
         * Custom Finalize Order
         * Should be overwritten in cart page
         */
        afterSuccessFinalizeOrder() {
        
        },

        /**
         * Complete purchase and redirect to payment page
         * 
         * @param {string} storageKey - key of localStorage object, for access to it in repayment
         * @param {object} params - payment params
         * @param {string} callbackRouteName - route name that redirect to it, after successful or unsuccessful payment
         * @returns {Promise}
         */
        paymentTransaction(storageKey = '', params = {}, callbackRouteName = 'payment') {
            this.$wait.start('loading-repayment-button');

            const callbackRoute = this.$router.resolve({ name: callbackRouteName }).href;
            const baseUrl = window.location.origin;

            const paramsInternal = {
                id          : this.model.id,
                path_id     : this.model.path_id,
                callback_url: baseUrl + callbackRoute,
                ...params,
            };

            localStorage.setItem(storageKey, JSON.stringify(paramsInternal));

            return this.$services.Cart.paymentTransactionMake(paramsInternal).then((response) => {
                this.afterSuccessPaymentTransaction(response);

                this.$toast.info(this.$t('cart.to_gateway'));
                window.location = response.data.results.url;
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('loading-repayment-button');
                this.$wait.end('loading-payment-button');
            });
        },

        /**
         * Custom Payment Transaction
         * Should be overwritten in cart page
         * 
         * @param {object} response
         */
        afterSuccessPaymentTransaction(response) { // eslint-disable-line no-unused-vars
        
        },

        /**
         * Calculate wares price
         */
        updateWaresPrice() {
            if (this.myCart && this.myCart.length > 0) {
                this.invoiceModel.wares_price = this.myCart[0].items
                    .reduce((previousValue, currentItem) => previousValue
                    + (currentItem.count
                        * (this.warePrice(currentItem) + this.wareTax(currentItem))),
                    0);

                this.invoiceModel.discount = this.myCart[0].items
                    .reduce((previousValue, currentItem) => previousValue
                    + (currentItem.count
                        * this.wareDiscount(currentItem)),
                    0);
            }
        },

        /**
         * Return ware price
         *
         * @param {object} ware
         * @returns {string | number}
         */
        warePrice(ware) {
            if (ware.single_price) {
                return ware.single_price;
            }
            if (ware.sale_price) {
                return ware.sale_price;
            }
            if (ware.order_price) {
                return ware.order_price.price;
            }
            return 0;
        },

        /**
         * Return ware discount
         *
         * @param {object} ware
         * @returns {string | number}
         */
        wareDiscount(ware) {
            if (ware.single_discount) {
                return ware.single_discount;
            }
            if (ware.order_price) {
                return ware.order_price.discount;
            }
            return 0;
        },

        /**
         * Return ware tax
         *
         * @param {object} ware
         * @returns {string | number}
         */
        wareTax(ware) {
            if (ware.single_tax) {
                return ware.single_tax;
            }
            if (ware.order_price) {
                return ware.order_price.tax;
            }
            return 0;
        },
    },
};
